/**
 * ArbitrageResultItem Component
 *
 * Renders an arbitrage search result item with premium styling.
 * Displays name, project, status badge, and star icon for watchlist.
 */

import { StarFilled, StarOutlined, SwapOutlined } from '@ant-design/icons';
import cn from 'classnames';
import React from 'react';

import type { ArbitrageSearchResult } from '../types';

interface ArbitrageResultItemProps {
  /** Arbitrage search result data */
  result: ArbitrageSearchResult;

  /** Whether this item is currently focused via keyboard navigation */
  isFocused: boolean;

  /** Whether this item is in the user's watchlist */
  isFavorite?: boolean;

  /** Callback when item is clicked */
  onClick: () => void;

  /** Callback when star icon is clicked */
  onToggleFavorite?: () => void;
}

export const ArbitrageResultItem: React.FC<ArbitrageResultItemProps> = ({
  result,
  isFocused,
  isFavorite = false,
  onClick,
  onToggleFavorite,
}) => {
  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.();
  };

  return (
    <div
      id={`result-${result.id}`}
      role="option"
      aria-selected={isFocused}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-all duration-150 text-[#eef1ff] cursor-pointer focus-visible:ring-2 focus-visible:ring-indigo-500/50',
        {
          'bg-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]': isFocused,
          'hover:bg-white/5': !isFocused,
          'opacity-60': result.isArchived,
        },
      )}
      tabIndex={0}
    >
      <div className="flex flex-1 items-center gap-3 overflow-hidden">
        <div
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-150',
            {
              'bg-pink-500/20': isFocused,
              'bg-pink-500/10': !isFocused,
            },
          )}
        >
          <SwapOutlined className="text-pink-400" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-[#f6f7ff]">{result.name}</span>
            {result.isArchived && (
              <span className="shrink-0 rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-400">
                Archived
              </span>
            )}
          </div>
          <span className="truncate text-xs text-[#9ca3af]">{result.projectName}</span>
        </div>
      </div>

      {/* Right content - responsive width */}
      <div className="flex w-[70px] sm:w-[100px] shrink-0 items-center justify-end gap-2 sm:gap-3">
        <div className="hidden sm:flex min-w-[44px] items-center justify-center gap-1.5">
          <span
            className={cn('text-[10px]', {
              'text-green-400': result.status === 'active',
              'text-gray-500': result.status === 'inactive',
            })}
          >
            {result.status === 'active' ? '●' : '○'}
          </span>
          <span className="rounded-lg bg-pink-500/20 px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-pink-300">
            ARB
          </span>
        </div>

        <button
          type="button"
          tabIndex={0}
          onClick={handleStarClick}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full transition-all duration-150',
            'hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50',
            {
              'text-orange-400': isFavorite,
              'text-gray-500 hover:text-orange-300': !isFavorite,
            },
          )}
          aria-label={isFavorite ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {isFavorite ? <StarFilled className="text-sm" /> : <StarOutlined className="text-sm" />}
        </button>
      </div>
    </div>
  );
};
